import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import { TextField } from '@trivie/ui-web/components/text-field'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useStores, validateAndFormatNumber } from '@trivie/core'
import { AuthenticationApi } from '@trivie/core/src/services/api-objects'

import { Divider } from '@trivie/ui-mobile/components/divider'
import { SocialLoginButtons } from '@trivie/ui-mobile/components/social-login-buttons'
import { Button } from '@trivie/ui-mobile/components/button'
import { Text } from '@trivie/ui-mobile/components/text'
import { color, iconLock, SvgIcon } from '@trivie/ui-mobile'

export const MagicLinkScreen = observer(() => {
  const { userStore } = useStores()
  const { sharedQuiz } = userStore
  const { setPhoneNumber, setFormattedPhoneNumber } = userStore
  const history = useHistory()
  const { control, watch, handleSubmit } = useForm()
  const { login } = watch()

  const { t } = useTranslation()
  const formPlaceholder = t('Onboarding.Login.yourEmailHerePlaceholder')

  const sendMagicLink = (data: any) => {
    if (sharedQuiz) {
      userStore.setEmail(data.login)
      AuthenticationApi.loginShared(
        userStore.sharedQuiz?.code,
        userStore.sharedQuiz?.workspace_id,
        userStore.sharedQuiz?.quiz_id,
        data.login,
      ).then((resp) => {
        if (resp.data.token) {
          if (resp.data.metadata) {
            userStore.setSharedQuiz({ ...sharedQuiz, metadata: resp.data.metadata })
          }
          userStore.setToken(resp.data.token)
          userStore.getUser(resp.data.user_id).then(() => {
            // check for first / last name -> then go to game
            history.push(
              `/game/quiz/?assignment_id=${userStore.sharedQuiz?.metadata?.assignment_id}&topic_id=${userStore.sharedQuiz?.metadata?.topic_id}&quiz_id=${userStore.sharedQuiz?.quiz_id}&path_id=${userStore.sharedQuiz?.metadata?.path_id}&content_user_id=${userStore.sharedQuiz?.metadata?.content_user_id}`,
              {
                from: 'share',
                quiz: { topic_id: null },
              },
            )
          })
        } else {
          // new user
          // show one time code
          history.push('/login/magic/sent')
        }
      })
    } else {
      if (!data.login.includes('@') && !data.login.includes('.')) {
        setPhoneNumber(data.login)
        setFormattedPhoneNumber(validateAndFormatNumber(data.login) ?? '')
        userStore
          .sendMagicLink(validateAndFormatNumber(data.login) ?? '', 'engage', true)
          .then((res: any) => {
            if (res.data.redirect_url) {
              localStorage.setItem('redirecting-sso', 'true')
              window.location.replace(res.data.redirect_url)
            } else {
              history.push('/login/sms')
            }
          })
      } else if (data.login.includes('@') && data.login.includes('.')) {
        userStore.sendMagicLink(data.login).then((res: any) => {
          userStore.setEmail(data.login)
          if (res.data.redirect_url) {
            localStorage.setItem('redirecting-sso', 'true')
            window.location.replace(res.data.redirect_url)
          } else {
            history.push('/login/magic/sent')
          }
        })
      }
    }
  }

  const [validated, setValidated] = useState(false)

  useEffect(() => {
    const handleEnter = (event) => {
      if (event.keyCode === 13) {
        handleSubmit(sendMagicLink)()
      }
    }
    window.addEventListener('keydown', handleEnter)

    return () => {
      window.removeEventListener('keydown', handleEnter)
    }
  }, [])

  return (
    <div style={ROOT}>
      <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
        <Text align="center" tx="Onboarding.Login.logIn" variant="headline" />
        <Text
          align="center"
          variant="titleMedium"
          style={{ color: color.shade50 }}
          tx="Onboarding.Login.useMagicLink"
        />
      </div>
      <Controller
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value }) => (
          <TextField
            fullWidth
            required
            inputProps={{ inputMode: 'email' }}
            onChange={(e) => {
              onChange(e.target.value)
              if (
                validateAndFormatNumber(e.target.value) ||
                (e.target.value.includes('@') && e.target.value.includes('.'))
              ) {
                setValidated(true)
              } else {
                setValidated(false)
              }
            }}
            onBlur={onBlur}
            type="email"
            value={value}
            style={{ marginBottom: 32 }}
            size="small"
            helperText={
              login && !validated
                ? sharedQuiz
                  ? 'Enter a valid email address.'
                  : 'Enter a valid phone number or email address.'
                : ''
            }
            placeholder={formPlaceholder}
            error={login && !validated}
          />
        )}
        name="login"
        rules={{ required: true }}
      />
      <Button
        disabled={!validated}
        fullWidth
        tx="Common.continue"
        onPress={handleSubmit(sendMagicLink)}
        accessibilityRole="button"
        accessibilityLabel="Send Magic Link"
      />
      <Divider
        color={color.shade20}
        split={<Text variant="titleEmphasized" tx="Common.or" style={{ marginHorizontal: 10 }} />}
      />
      <SocialLoginButtons
        appleLogin={() =>
          window.location.replace(`${process.env.REACT_APP_SSO_APPLE_URL}?intent=signin`)
        }
        googleLogin={() =>
          window.location.replace(`${process.env.REACT_APP_SSO_GOOG_URL}?intent=signin`)
        }
        ssoLogin={() => history.push('/login/sso')}
      />
    </div>
  )
})

const ROOT: React.CSSProperties = {
  height: '100%',
  maxHeight: 575,
  width: 335,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  margin: 'auto',
}
